import React from "react";
import { Link } from "react-router-dom";

/**
 * Shared software product card — used by both the homepage software row
 * and the dedicated /software page. Same visual DNA as ProductCard but
 * a different content shape (centered logo + name + subtitle, no
 * description/price/CTA), so kept as its own component rather than
 * forced into ProductCard's image-on-top layout.
 *
 * Props:
 *   logo      required — image URL
 *   name      required
 *   subtitle  optional
 *   testId    optional
 *   size      "compact" (homepage) | "comfortable" (dedicated page)
 *   href      optional — when set, the entire card becomes a link to the
 *             software detail page (opens in the same tab). Uses
 *             react-router <Link> so client-side navigation is preserved.
 */
export const SoftwareCard = ({ logo, name, subtitle, testId, size = "compact", href }) => {
  const isComfortable = size === "comfortable";
  const padding = isComfortable ? "p-7" : "p-6";
  const logoBox = isComfortable ? "w-16 h-16" : "w-14 h-14";
  // Compact initials shown when a product has no uploaded logo yet — avoids
  // the broken-image icon while preserving the layout / card dimensions.
  const initials = (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  const Wrapper = href ? Link : "div";
  const wrapperProps = href
    ? { to: href, "aria-label": `${name} — view details` }
    : {};
  return (
    <Wrapper
      data-testid={testId}
      {...wrapperProps}
      className={`bg-[#151515] rounded-2xl border border-[#262626] ${padding} flex flex-col items-center text-center hover:border-[#FF6600]/40 ur-card-lift h-full w-full ${
        href
          ? "cursor-pointer focus:outline-none focus-visible:border-[#FF6600] focus-visible:ring-2 focus-visible:ring-[#FF6600]/40"
          : ""
      }`}
    >
      <div className={`${logoBox} grid place-items-center mb-3 shrink-0`}>
        {logo ? (
          <img src={logo} alt={name} className="max-w-full max-h-full object-contain" />
        ) : (
          <div
            aria-label={name}
            className="w-full h-full grid place-items-center rounded-xl bg-[#1F1F1F] border border-[#262626] text-[#FF6600] font-black text-lg tracking-wide"
          >
            {initials}
          </div>
        )}
      </div>
      {/* Reserve 2 lines for the name so single-line and two-line titles
          leave the subtitle starting at the exact same y-position across
          every card in the row. */}
      <div
        className={`font-bold flex items-center justify-center ${isComfortable ? "text-white min-h-[3rem]" : "text-sm text-white min-h-[2.5rem]"}`}
      >
        <span className="line-clamp-2 leading-tight">{name}</span>
      </div>
      {/* Reserve 2 lines for the subtitle too — always mounted so every
          card has the same total height even when the subtitle is empty
          or short. Uses invisible content as a fallback so vertical
          rhythm stays identical. */}
      <div className="text-xs text-[#A3A3A3] mt-0.5 min-h-[2rem] leading-tight">
        <span className="line-clamp-2">{subtitle || "\u00A0"}</span>
      </div>
    </Wrapper>
  );
};
